import type { AnimationGroup, Scene, TransformNode } from '@babylonjs/core';
import { createLogger, isLoopingAction, type ActionQualifiers, type AnimationVocabulary, type BodyPartModifier, type CharacterActionId, type NpcModelState, type Vector2, type WorldSpace } from '@sorrem/shared';
import { AnimationVariantPlayer } from './animation-variant-player';
import { resolveActionClip, resolveCharacterAnimationContext, resolveModelRemapTiers, type CharacterAnimationContext } from './character-action-playback';
import { cloneRetargetedAnimationGroups } from './character-animation-base';
import type { CharacterLoader } from './character-loader';
import { updateCharacterTransform, type LoadedCharacterMesh } from './character-mesh';
import { registerDynamicSockets, type DynamicSocketHandle } from './dynamic-sockets';
import { attachEquipmentToCharacter, attachHeldModel, type AssetCache, type AttachedEquipmentHandle } from './equipment-mesh-loader';
import type { ItemLooks } from '../vfx/item-effect';
import { TILE_UNIT_SIZE } from '../scale-constants';

const logger = createLogger('engine:character');

export interface CharacterOptions {
    readonly characterLoader: CharacterLoader;
    readonly equipmentCache: AssetCache;
    readonly bodyPartCache: AssetCache;
    /** Loads models passed to `setHeldItem`, keyed by model path; without it `setHeldItem` does nothing. */
    readonly heldItemCache?: AssetCache;
    readonly assetBaseUrl: string;
    /** Seed for this character's own `AnimationVariantPlayer` - pass something stable per entity (e.g. entityKey/accountId). */
    readonly seed: string;
    /** The clock its death is timed by, in milliseconds; the page's (`performance.now`) when left out. */
    readonly now?: () => number;
}

/** A model held for a moment in an attachment slot's socket (see `Character.setHeldItem`). */
export interface HeldItem {
    /** The attachment slot whose socket holds it, e.g. `weapon-off` for the left hand. */
    readonly slotId: string;
    /** The model's path under `game-content/assets/`. */
    readonly modelPath: string;
    /** Hex color the model is tinted with, and glows and haloes in. */
    readonly tintColor?: string;
}

/** What a semantic action resolved to the last time `Character.playAction` was asked for it. */
export interface PlayedActionClip {
    readonly action: CharacterActionId;
    /** The vocabulary key played, or null when nothing in this rig's vocabulary fits the action. */
    readonly key: string | null;
    readonly loop: boolean;
    readonly blendDurationMs: number;
    /** The chosen clip's real length at 1x speed, in ms; null when nothing played. */
    readonly durationMs: number | null;
}

/**
 * A single loaded, animatable character: its own mesh/skeleton, the shared
 * per-skeleton-type animation base retargeted onto its joints, and any
 * equipped item meshes - the class-based consolidation of what
 * `character-loader.ts`/`character-mesh.ts`/`character-animation-base.ts`/
 * `equipment-mesh-loader.ts`/`animation-variant-player.ts` each implement
 * separately. Those modules stay as the implementation this class calls;
 * this is the single owner of one loaded instance's lifecycle.
 *
 * One `Character` per spawned entity, for that entity's lifetime - `load()`
 * is one-shot; construct a new `Character` (and `dispose()` the old one) to
 * load a different model.
 *
 * Note: `@sorrem/shared` also exports a `Character` type (the player/NPC
 * data model). This is a different, unrelated `Character` from
 * `@sorrem/engine` - a call site that needs both must alias one import.
 */
export class Character {
    private readonly scene: Scene;
    private readonly characterLoader: CharacterLoader;
    private readonly equipmentCache: AssetCache;
    private readonly bodyPartCache: AssetCache;
    private readonly heldItemCache: AssetCache | null;
    private readonly assetBaseUrl: string;
    private readonly seed: string;
    private readonly now: () => number;

    private mesh: LoadedCharacterMesh | null = null;
    private vocab: AnimationVocabulary | null = null;
    private equipmentHandles: readonly AttachedEquipmentHandle[] = [];
    private characterModelId: string | null = null;
    private scale = 1;
    /** How far its idle pose needs raising (negative: lowering) to stand on the ground, before its scale (`CharacterModel.groundOffset`). */
    private groundOffset = 0;
    /** How far its fallen body needs lifting to lie on the floor, before its scale (`CharacterModel.lyingLift`). */
    private lyingLift = 0;
    /** When its current death began (`now`) and how long the clip runs, while it is dying or dead. */
    private death: { readonly startedAtMs: number; readonly durationMs: number } | null = null;
    private animationPlayer: AnimationVariantPlayer | null = null;
    private dynamicSockets: DynamicSocketHandle | null = null;
    /** The held item asked for last (`handle` stays null while it loads), or null when nothing is held. */
    private heldItem: { readonly key: string; readonly slotId: string; handle: AttachedEquipmentHandle | null } | null = null;
    /** Bumped on every held-item change, so a load that finishes after a newer change is dropped. */
    private heldItemRequest = 0;
    /** The last `playAction` call's clip, for tools that show what an action resolved to (the Character Debugger). */
    private lastPlayedClip: PlayedActionClip | null = null;
    /** Bumped on every equipment change (and by `load`/`dispose`), so gear that finishes loading after a newer change is dropped. */
    private equipmentRequest = 0;

    constructor(scene: Scene, options: CharacterOptions) {
        this.scene = scene;
        this.characterLoader = options.characterLoader;
        this.equipmentCache = options.equipmentCache;
        this.bodyPartCache = options.bodyPartCache;
        this.heldItemCache = options.heldItemCache ?? null;
        this.assetBaseUrl = options.assetBaseUrl;
        this.seed = options.seed;
        this.now = options.now ?? ((): number => performance.now());
    }

    /**
     * Loads the base mesh, retargets the shared animation base onto it, and
     * (if given) attaches equipment - all atomically: the mesh root stays
     * disabled until every step below has resolved, then is enabled once, in
     * a single reveal. Passing `equippedItemDefinitionIds` up front (rather
     * than a separate `updateEquipment()` call right after `load()`
     * resolves) is what keeps gear from visibly popping in a frame after the
     * bare body.
     */
    async load(
        npcData: NpcModelState,
        equippedItemDefinitionIds: readonly string[] = [],
        outfitModifiers?: Partial<Record<string, BodyPartModifier>>,
        itemLooks?: ItemLooks,
    ): Promise<void> {
        const characterModelId = npcData.characterModelId;
        // A later load() or dispose() bumps this: this load's result is then stale.
        const request = ++this.equipmentRequest;
        const loaded = await this.characterLoader.loadCharacter(this.scene, npcData);

        // See character-mesh-loading.ts's original comment on this pattern:
        // loadCharacter imports the mesh fully enabled at its raw import
        // transform, with real awaited I/O (animation retarget, equipment
        // attachment) still left below - disabling now and enabling once at
        // the end makes this method's own promise the single "fully ready"
        // signal, with no intermediate visible state.
        loaded.root.setEnabled(false);

        let retargeted = loaded;
        let vocab: AnimationVocabulary;
        let equipment: readonly AttachedEquipmentHandle[];
        try {
            if (npcData.textureVariantId !== undefined) {
                // A missing variant texture leaves its own colors on, not a broken character.
                await this.characterLoader
                    .applyTextureVariant(this.scene, characterModelId, npcData.textureVariantId, loaded.meshes)
                    .catch((error: unknown) => logger.warn(`Character: texture variant ${npcData.textureVariantId} of ${characterModelId} failed`, error));
            }

            const [animationBase, loadedVocab] = await Promise.all([
                this.characterLoader.loadAnimationBase(this.scene, characterModelId),
                this.characterLoader.loadAnimationVocabulary(characterModelId),
            ]);
            vocab = loadedVocab;

            const retargetedGroups = cloneRetargetedAnimationGroups(animationBase.animationGroups, loaded, animationBase);
            for (const group of loaded.animationGroups) {
                group.dispose();
            }
            retargeted = { ...loaded, animationGroups: retargetedGroups };

            equipment = equippedItemDefinitionIds.length > 0
                ? await attachEquipmentToCharacter(this.equipmentCache, this.bodyPartCache, this.characterLoader, this.scene, characterModelId, retargeted, equippedItemDefinitionIds, this.assetBaseUrl, outfitModifiers, itemLooks)
                : [];
        } catch (error) {
            // Never assigned to this character, so nothing else would free it.
            disposeLoadedMesh(retargeted);
            throw error;
        }

        if (request !== this.equipmentRequest) {
            // Disposed or loaded again meanwhile: a late reveal would be a zombie.
            equipment.forEach((handle) => handle.dispose());
            disposeLoadedMesh(retargeted);
            return;
        }

        this.mesh = retargeted;
        this.vocab = vocab;
        this.equipmentHandles = equipment;
        this.characterModelId = characterModelId;
        const model = this.characterLoader.findCharacterModel(characterModelId);
        this.scale = model?.scale ?? 1;
        // Baked by the model pipeline from its idle and death poses, so a
        // bent-legged creature stands on the floor and a thick one lies on
        // it. Applied here for callers that never place the root (the
        // previews) and again by updateTransform, which places it every frame.
        this.groundOffset = model?.groundOffset ?? 0;
        this.lyingLift = model?.lyingLift ?? 0;
        retargeted.root.position.y += this.groundOffset * this.scale;
        this.death = null;
        this.animationPlayer = new AnimationVariantPlayer(this.seed);

        this.dynamicSockets?.dispose();
        const skeletonType = this.characterLoader.findSkeletonType(characterModelId);
        this.dynamicSockets = skeletonType ? registerDynamicSockets(this.scene, retargeted, skeletonType) : null;

        // Revealed already standing in its idle (through its model's remaps:
        // a beast's idle is its own crouch), never in the import's bind pose
        // blending into one. A caller that knows it should be in something
        // else (a corpse) poses that before the next frame: `poseAction`.
        this.poseAction('idle');
        retargeted.root.setEnabled(true);
    }

    /**
     * Live re-equip on an already-visible character - replaces whatever was
     * attached before. Unlike `load()`, this never hides/re-shows the root:
     * a mid-game gear swap is a different operation from the initial atomic
     * reveal, not a reason to hide the whole character. The old gear stays on
     * until the new gear has fully loaded, then the two swap in one step, so
     * a slow (e.g. first-time, uncached) model never leaves a slot empty.
     */
    async updateEquipment(
        equippedItemDefinitionIds: readonly string[],
        outfitModifiers?: Partial<Record<string, BodyPartModifier>>,
        itemLooks?: ItemLooks,
    ): Promise<void> {
        if (!this.mesh || !this.characterModelId) {
            return;
        }
        const request = ++this.equipmentRequest;
        const handles = await attachEquipmentToCharacter(
            this.equipmentCache,
            this.bodyPartCache,
            this.characterLoader,
            this.scene,
            this.characterModelId,
            this.mesh,
            equippedItemDefinitionIds,
            this.assetBaseUrl,
            outfitModifiers,
            itemLooks,
        );
        if (request !== this.equipmentRequest) {
            handles.forEach((handle) => handle.dispose());
            return;
        }
        this.equipmentHandles.forEach((handle) => handle.dispose());
        this.equipmentHandles = handles;
        // Re-equipping mid-hold must not show the new item through the held one.
        if (this.heldItem?.handle) {
            this.setSlotEquipmentEnabled(this.heldItem.slotId, false);
        }
    }

    /**
     * Shows `held` in its slot's socket (e.g. a crystal in the left hand
     * while it is being used), hiding whatever is equipped in that slot until
     * it is cleared; `null` clears it. Cheap to call every frame: asking for
     * what is already held (or still loading) does nothing.
     */
    async setHeldItem(held: HeldItem | null): Promise<void> {
        const key = held ? `${held.slotId}|${held.modelPath}|${held.tintColor ?? ''}` : null;
        if ((this.heldItem?.key ?? null) === key) {
            return;
        }
        const request = ++this.heldItemRequest;
        this.clearHeldItem();
        if (!held || !key || !this.mesh || !this.characterModelId || !this.heldItemCache) {
            return;
        }

        const boneName = this.characterLoader.findSkeletonType(this.characterModelId)
            ?.attachmentSlots.find((slot) => slot.id === held.slotId)?.boneName;
        // Recorded before loading, so per-frame calls for the same item wait on this load.
        const pending: { key: string; slotId: string; handle: AttachedEquipmentHandle | null } = { key, slotId: held.slotId, handle: null };
        this.heldItem = pending;
        if (!boneName) {
            return;
        }

        const handle = await attachHeldModel(this.heldItemCache, this.scene, this.mesh, held.slotId, boneName, held.modelPath, held.tintColor);
        if (request !== this.heldItemRequest) {
            handle?.dispose();
            return;
        }
        pending.handle = handle;
        if (handle) {
            this.setSlotEquipmentEnabled(held.slotId, false);
        }
    }

    private clearHeldItem(): void {
        if (!this.heldItem) {
            return;
        }
        if (this.heldItem.handle) {
            this.heldItem.handle.dispose();
            this.setSlotEquipmentEnabled(this.heldItem.slotId, true);
        }
        this.heldItem = null;
    }

    private setSlotEquipmentEnabled(slotId: string, enabled: boolean): void {
        this.equipmentHandles
            .filter((handle) => handle.slotId === slotId)
            .forEach((handle) => handle.root.setEnabled(enabled));
    }

    /**
     * Plays a *semantic* action (`attack`, `move`, `dodge`, ...) rather than a
     * named clip, resolving which clip that means for this particular
     * character through `resolveActionAnimationCandidates`.
     *
     * This is the preferred entry point: it is the only place that knows both
     * the authored `actionAnimationMap` override tiers (equipped weapon ->
     * character model -> skeleton type) and which clips this rig's
     * vocabulary actually contains, so it can walk the candidate list and
     * play the first one that exists. Playing a raw clip name skips
     * all of that and plays nothing at all when the key is absent - a
     * *frozen* character, since a missing vocabulary entry yields zero
     * variants.
     *
     * `qualifiers` need only what this character's equipment cannot tell
     * (movement tier, crystal size, an attack's motion, and an NPC's weapon
     * class and stance); the rest comes from what it is holding, see
     * `resolveCharacterAnimationContext`.
     *
     * `blendDurationMs` defaults to the action's own blend time rather than
     * one flat value for everything - a 150ms swing and a looping walk cycle
     * need very different blends, and a blend longer than its own clip means
     * the clip never reaches its keyposes. See `resolveActionBlendDurationMs`.
     *
     * `fitDurationMs` plays the whole chosen clip in that many ms instead of
     * at `speedRatio` (see `AnimationVariantPlayer.play`).
     *
     * `instance` tells one playing of the same action from the next (a
     * combo's next swing): a new one starts its clip again from the top.
     *
     * Returns the chosen clip's real duration in ms at 1x speed, or `null`
     * when nothing could be played (not loaded yet, or an empty vocabulary).
     */
    playAction(
        action: CharacterActionId,
        qualifiers: ActionQualifiers = {},
        speedRatio = 1,
        blendDurationMs?: number,
        fitDurationMs?: number,
        instance?: string,
    ): number | null {
        if (!this.mesh || !this.animationPlayer || !this.vocab) {
            return null;
        }

        const context = this.resolveAnimationContext(action, qualifiers);
        const resolved = resolveActionClip(
            action,
            context.qualifiers,
            this.vocab,
            context.overrideMaps,
            blendDurationMs,
        );
        if (!resolved) {
            this.lastPlayedClip = { action, key: null, loop: false, blendDurationMs: blendDurationMs ?? 0, durationMs: null };
            return null;
        }

        const durationMs = this.animationPlayer.play(
            this.mesh,
            resolved.key,
            resolved.variants,
            resolved.loop,
            speedRatio,
            resolved.blendDurationMs,
            fitDurationMs,
            instance,
        );
        this.lastPlayedClip = { action, key: resolved.key, loop: resolved.loop, blendDurationMs: resolved.blendDurationMs, durationMs };
        if (action !== 'death') {
            this.death = null;
        } else if (!this.death) {
            this.death = { startedAtMs: this.now(), durationMs: (durationMs ?? 0) / Math.max(speedRatio, 0.0001) };
        }
        return durationMs;
    }

    /**
     * What it is playing now: the clip (the very variant), the action it
     * plays it for, and how fast. Undefined before it loads, or with nothing
     * playing. For an effect that records what it did, to replay it (Echo's
     * ghost).
     */
    findPlayingClip(): { readonly action: CharacterActionId; readonly group: AnimationGroup; readonly speedRatio: number } | undefined {
        const name = this.animationPlayer?.activeGroup;
        const group = name ? this.mesh?.animationGroups.find((candidate) => candidate.name === name) : undefined;
        if (!group || !this.lastPlayedClip) {
            return undefined;
        }
        return { action: this.lastPlayedClip.action, group, speedRatio: group.speedRatio };
    }

    /**
     * The clip this character plays for `action`, as `playAction` would pick
     * it: the variant it is playing (or last played), if that is this
     * action's, else the action's first. Undefined before it loads, or with no
     * clip for it. For an effect that replays what it did (Echo's ghost).
     */
    findActionAnimationGroup(action: CharacterActionId, qualifiers: ActionQualifiers = {}): AnimationGroup | undefined {
        if (!this.mesh || !this.vocab) {
            return undefined;
        }
        const context = this.resolveAnimationContext(action, qualifiers);
        const resolved = resolveActionClip(action, context.qualifiers, this.vocab, context.overrideMaps);
        if (!resolved) {
            return undefined;
        }
        const active = this.animationPlayer?.activeGroup;
        const name = resolved.variants.some((variant) => variant.name === active) ? active : resolved.variants[0]?.name;
        return this.mesh.animationGroups.find((group) => group.name === name);
    }

    /**
     * Puts it straight into the pose `action` holds, with no blend: for a
     * character appearing on screen in the middle of something, rather than
     * blending into it out of whatever pose its joints were left in. A held
     * state starts at its first frame (an idle, a walk, a channel's loop)
     * and a death at its last, already lying still. Any other one-shot (a
     * swing, a flinch) is not a pose to appear in: it stands in its idle,
     * and the `playAction` that follows blends into the one-shot as usual.
     *
     * A `playAction` of the same action afterwards carries on from here
     * rather than starting the clip over (a corpse does not fall again).
     * `load()` poses its idle on its own; no-ops before `load()` resolves.
     */
    poseAction(action: CharacterActionId, qualifiers: ActionQualifiers = {}): void {
        if (!this.mesh || !this.animationPlayer || !this.vocab) {
            return;
        }
        const isDeath = action === 'death';
        const posed: CharacterActionId = isDeath || isLoopingAction(action, qualifiers) ? action : 'idle';
        // An idle stood in for a one-shot keeps only how it holds its gear.
        const posedQualifiers: ActionQualifiers = posed === action
            ? qualifiers
            : (qualifiers.stance !== undefined ? { stance: qualifiers.stance } : {});
        const context = this.resolveAnimationContext(posed, posedQualifiers);
        const resolved = resolveActionClip(posed, context.qualifiers, this.vocab, context.overrideMaps);
        if (!resolved) {
            return;
        }
        const durationMs = this.animationPlayer.cut(this.mesh, resolved.key, resolved.variants, resolved.loop, resolved.blendDurationMs, isDeath ? 'end' : 'start');
        this.lastPlayedClip = { action: posed, key: resolved.key, loop: resolved.loop, blendDurationMs: 0, durationMs };
        // Already fallen: its lying lift applies in full at once.
        this.death = isDeath ? { startedAtMs: this.now() - (durationMs ?? 0), durationMs: durationMs ?? 0 } : null;
    }

    /**
     * Holds the clip it is playing on the frame it has reached, or carries on
     * from there: the character preview's play/pause. No-ops with nothing
     * playing.
     */
    setAnimationPaused(paused: boolean): void {
        const name = this.animationPlayer?.activeGroup;
        const group = name ? this.mesh?.animationGroups.find((candidate) => candidate.name === name) : undefined;
        if (!group) {
            return;
        }
        if (paused) {
            group.pause();
        } else if (!group.isPlaying) {
            // Resumes from the paused frame.
            group.play();
        }
    }

    /** This character's qualifiers and remap tiers for one action, from its equipment and model. */
    private resolveAnimationContext(action: CharacterActionId, qualifiers: ActionQualifiers): CharacterAnimationContext {
        const modelId = this.characterModelId;
        return resolveCharacterAnimationContext(
            action,
            qualifiers,
            this.equipmentHandles.map((handle) => ({
                slotId: handle.slotId,
                piece: this.characterLoader.findEquipmentPiece(handle.itemDefinitionId),
            })),
            modelId ? resolveModelRemapTiers(this.characterLoader.findCharacterModel(modelId), this.characterLoader.findSkeletonType(modelId)) : [],
        );
    }

    /** Positions/scales/orients this character's root. `scale` defaults to the character model's own catalog scale (see `getScale()`) when omitted. No-ops before `load()` resolves. */
    updateTransform(worldPosition: WorldSpace, facing: Vector2, tileUnitSize = TILE_UNIT_SIZE, levelHeight = 0, scale?: number): void {
        if (!this.mesh) {
            return;
        }
        const appliedScale = scale ?? this.scale;
        const lift = this.groundOffset + this.lyingLift * this.resolveDeathProgress();
        updateCharacterTransform(this.mesh.root, worldPosition, facing, tileUnitSize, levelHeight + lift * appliedScale, appliedScale);
    }

    /**
     * How far through its death clip it is, 0 to 1 (1 once it lies still);
     * 0 while alive. The lying lift eases in with it, so the body settles
     * onto the floor as it falls instead of popping up.
     */
    private resolveDeathProgress(): number {
        if (!this.death) {
            return 0;
        }
        return this.death.durationMs > 0 ? Math.min(1, (this.now() - this.death.startedAtMs) / this.death.durationMs) : 1;
    }

    /** Shows/hides this character's root. No-ops before `load()` resolves. */
    setVisible(visible: boolean): void {
        this.mesh?.root.setEnabled(visible);
    }

    getRoot(): TransformNode {
        return this.requireMesh().root;
    }

    getMesh(): LoadedCharacterMesh {
        return this.requireMesh();
    }

    getVocab(): AnimationVocabulary {
        if (!this.vocab) {
            throw new Error('Character: not loaded yet - call load() first.');
        }
        return this.vocab;
    }

    /** What the last `playAction` call resolved to; null before one has run on the loaded character. */
    getLastPlayedClip(): PlayedActionClip | null {
        return this.lastPlayedClip;
    }

    getEquipmentHandles(): readonly AttachedEquipmentHandle[] {
        return this.equipmentHandles;
    }

    getScale(): number {
        return this.scale;
    }

    isLoaded(): boolean {
        return this.mesh !== null;
    }

    /** Resolves a named bone/socket TransformNode - the same lookup equipment attachment already uses. */
    findBone(boneName: string): TransformNode | undefined {
        return this.mesh?.transformNodes.find((node) => node.name === boneName);
    }

    /**
     * VFX attachment hook: parents `effect` under a named bone (falling back
     * to the character's root if the bone isn't found) or the root itself
     * when no bone is given - a direct parent reference rather than a
     * lookup by name.
     * Callers are equally free to just call `getRoot()`/`findBone()` and
     * parent an effect themselves; this is a convenience, not a requirement.
     */
    attachEffect(effect: TransformNode, boneName?: string): void {
        const parent = (boneName && this.findBone(boneName)) || this.mesh?.root;
        effect.parent = parent ?? null;
    }

    /** Steps its gear's effects (an item's glow and layers, a held model's halo) by `deltaSeconds`; its owner calls it every frame, before the frame is drawn. */
    update(deltaSeconds: number): void {
        this.equipmentHandles.forEach((handle) => handle.update(deltaSeconds));
        this.heldItem?.handle?.update(deltaSeconds);
    }

    /**
     * Disposes this character's mesh, its own animation groups (the clones
     * retargeted from the shared base) and skeletons, and attached equipment.
     * Does not dispose the injected `CharacterLoader`/`AssetCache`s/`Scene`
     * or the animation base - those are shared and caller-owned.
     */
    dispose(): void {
        this.dynamicSockets?.dispose();
        this.dynamicSockets = null;
        this.heldItemRequest++;
        this.clearHeldItem();
        this.equipmentRequest++;
        this.equipmentHandles.forEach((handle) => handle.dispose());
        this.equipmentHandles = [];
        if (this.mesh) {
            disposeLoadedMesh(this.mesh);
        }
        this.mesh = null;
        this.vocab = null;
        this.animationPlayer = null;
    }

    private requireMesh(): LoadedCharacterMesh {
        if (!this.mesh) {
            throw new Error('Character: not loaded yet - call load() first.');
        }
        return this.mesh;
    }
}

/**
 * Disposes a loaded character's own pieces: its animation groups, skeletons,
 * meshes and root. Left behind, every despawned NPC or remote player would
 * keep its groups and skeleton in the scene for the rest of the run.
 */
function disposeLoadedMesh(mesh: LoadedCharacterMesh): void {
    mesh.animationGroups.forEach((group) => group.dispose());
    mesh.skeletons.forEach((skeleton) => skeleton.dispose());
    mesh.meshes.forEach((part) => part.dispose());
    mesh.root.dispose();
}
